import { useCallback } from 'react';
import { useCollectionsStore } from '../../store/collectionsStore';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { useAuthStore } from '../../store/authStore';
import {
  setActiveTab,
  setShowCollectionModal,
  setShowImportCollectionModal,
  setExportCollectionTarget,
  setShowExportCollectionModal,
  setCollectionRunnerAutoRun,
  setCollectionRunnerFolderId,
  setCollectionRunnerTarget,
  setShowCollectionRunnerModal,
  setCodeGenTarget,
  setShowCodeGenModal,
  setShowExportCollectionPickerModal,
} from '../../store/uiModalStore';
import { collectionAuthOf } from '../../utils/requestAssembly';
import { folderAuthChainForRequest } from '../../utils/requestFolderAuth';
import { isLocalWorkspace } from '../../utils/workspaceStorage';
import { formatCollectionRunResult } from '../../utils/collectionRunner';

/** Opening the create / import / export / run / code-generation modals, and the runner's close and complete. */
export function useCollectionModals({
  toastT,
  collectionRunnerTargetRef,
  collectionRunnerAiResolveRef,
}) {
  // ---- Modal / panel openers ------------------------------------------------
  const createCollection = useCallback(() => {
    setActiveTab('collections');
    setShowCollectionModal(true);
  }, []);

  const openImportCollection = useCallback(() => {
    setActiveTab('collections');
    setShowImportCollectionModal(true);
  }, []);

  const openExportCollection = useCallback((collection) => {
    if (!collection) return;
    setExportCollectionTarget(collection);
    setShowExportCollectionModal(true);
  }, []);

  const openRunCollection = useCallback((collection) => {
    if (!collection) return;
    setCollectionRunnerAutoRun(false);
    setCollectionRunnerFolderId(null);
    setCollectionRunnerTarget(collection);
    setShowCollectionRunnerModal(true);
  }, []);

  const openCodeGen = useCallback((request, collection) => {
    if (!request) return;
    // collectionId travels too: the generator needs that collection's
    // variables, and without them a collection-scoped auth variable comes out
    // as a literal {{name}} for the user to replace by hand.
    // The folder chain too: a request that inherits its folder's auth must
    // generate with it, as Send and Run collection do.
    setCodeGenTarget({
      request,
      collectionAuth: collectionAuthOf(collection),
      folderAuthChain: folderAuthChainForRequest(request, collection),
      collectionId: collection?.id,
    });
    setShowCodeGenModal(true);
  }, []);

  // ---- Collection runner lifecycle (AI-driven runs resolve via the ref) ----
  const handleCollectionRunnerClose = useCallback(() => {
    if (collectionRunnerAiResolveRef.current) {
      collectionRunnerAiResolveRef.current({ error: 'Run cancelled' });
      collectionRunnerAiResolveRef.current = null;
    }
    setCollectionRunnerAutoRun(false);
    setCollectionRunnerFolderId(null);
    setShowCollectionRunnerModal(false);
    setCollectionRunnerTarget(null);
  }, [collectionRunnerAiResolveRef]);

  const handleCollectionRunnerComplete = useCallback(({ results, cancelled }) => {
    const collection = collectionRunnerTargetRef.current;
    if (!collection || !collectionRunnerAiResolveRef.current) return;
    collectionRunnerAiResolveRef.current(
      formatCollectionRunResult(collection, results, { interactive: true, cancelled })
    );
    collectionRunnerAiResolveRef.current = null;
    setCollectionRunnerAutoRun(false);
  }, [collectionRunnerTargetRef, collectionRunnerAiResolveRef]);

  // ---- Export selection -----------------------------------------------------
  const getExportableCollections = useCallback(() => {
    const collections = useCollectionsStore.getState().collections;
    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    return collections.filter((collection) => {
      if (!isAuthenticated) return true;
      if (isLocalWorkspace(currentWorkspace, isAuthenticated)) return true;
      const id = String(collection.id);
      return /^\d+$/.test(id);
    });
  }, []);

  const openExportCollectionFromPalette = useCallback(() => {
    const exportable = getExportableCollections();
    if (exportable.length === 0) {
      toastT('toast.noCollectionsToExport', 'info');
      return;
    }
    if (exportable.length === 1) {
      openExportCollection(exportable[0]);
      return;
    }
    setShowExportCollectionPickerModal(true);
  }, [getExportableCollections, openExportCollection, toastT]);

  const handleSelectCollectionForExport = useCallback((selectedCollectionId) => {
    const selectedCollection = getExportableCollections().find(
      (collection) => String(collection.id) === String(selectedCollectionId)
    );
    setShowExportCollectionPickerModal(false);
    if (!selectedCollection) {
      toastT('toast.invalidCollectionSelection', 'warning');
      return;
    }
    openExportCollection(selectedCollection);
  }, [getExportableCollections, openExportCollection, toastT]);

  return {
    createCollection,
    openImportCollection,
    openExportCollection,
    openRunCollection,
    openCodeGen,
    handleCollectionRunnerClose,
    handleCollectionRunnerComplete,
    getExportableCollections,
    openExportCollectionFromPalette,
    handleSelectCollectionForExport,
  };
}
